import React, { useState, useRef, Suspense, useEffect } from 'react';
import Spinner from 'design-system/Spinner';

import { Dialog, DialogTitle, DialogContent, DialogActions } from 'design-system';
import ExampleHeader from './ExampleHeader';
import { makeStyles } from 'design-system/product/theme';

import TaskComponent from 'react-scale/components/Task/TaskComponent';
import { ShortcutsProvider } from 'react-scale/hooks/useShortcut';
import { TaskStatus } from '@scale/shared/defaults';
import Alert from 'design-system/Alert';
import { Button } from 'design-system/Button';

import { sampleTask } from '@scale/shared/selfServe/validationHelpers';
import { useSlate } from 'slate-react';
import { Transforms } from 'slate';
import API from '@scale/frontend-lib/dashboard-api';
import { useQueryClient } from 'react-query';
import _ from 'lodash';
import { CAPTION_PLACEHOLDER } from './ExampleLabelModal';
import { useInstructionTaskData } from './hooks';
import ErrorBoundary from 'dashboard/components/SelfServe/TaskSetup/RapidTaskErrorBoundary';
import useUnprocessedAttachments from 'react-scale/studio/hooks/useUnprocessedAttachments';

interface ExampleViewerProps {
  modalOpen: boolean;
  taskInfo: $FIXME;
  taskId: string;
  location: $FIXME;
  lastSelection: $FIXME;
  readOnly: boolean;
  setModalOpen: (arg: boolean) => void;
  hasOpenedExample?: (taskId: string, isGoodExample: boolean) => void;
  projectTemplateId?: string;
  handleUpdateExample?: $FIXME;
}

const ExampleViewer: React.FC<ExampleViewerProps> = ({
  modalOpen,
  taskInfo,
  taskId,
  location,
  lastSelection,
  readOnly,
  setModalOpen,
  hasOpenedExample,
  projectTemplateId,
  handleUpdateExample,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const taskRef = useRef<$FIXME>();
  const editor = useSlate();
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [taskProps, setTaskProps] = useState<any>(undefined);
  const { data: taskData } = useInstructionTaskData(taskId, readOnly, projectTemplateId);
  const { goodExample, caption } = taskInfo;
  const { params, response, type, projectId, status } = taskData;
  const [currentCaption, setCurrentCaption] = useState(caption);
  const [exampleType, setExampleType] = useState(goodExample ? 'good' : 'bad');
  const queryClient = useQueryClient();
  const { fetchUnprocessedAttachments } = useUnprocessedAttachments();

  const allowToEdit = !readOnly && taskProps && status === TaskStatus.Completed;
  useEffect(() => {
    let canceled = false;
    async function fetchData() {
      if (!loading && params && type && projectId) {
        setTaskProps(undefined);
        const taskParams = params;

        if (
          taskParams &&
          taskParams.attachments &&
          taskParams.attachments.length > 0 &&
          _.every(taskParams.attachments, a => a.type === 'unprocessed')
        ) {
          taskParams.attachments = await fetchUnprocessedAttachments(taskParams.attachments ?? []);
        }

        if (!canceled) {
          if (hasOpenedExample) {
            hasOpenedExample(taskId, goodExample);
          }

          setTaskProps({
            ...sampleTask,
            assignment: {
              params: taskParams,
              projectId,
              type,
            },
            type,
            params: taskParams,
            fixResponse: response,
            timerDisabled: true,
            shouldWarnOnExit: false,
            readOnly: !allowToEdit,
            editable: allowToEdit,
            isCustomer: true,
            showHeader: false,
            isTaxonomyDisplay: true,
            showFooter: false,
          });
        }
      }
    }

    fetchData();
    return function cleanup() {
      canceled = true;
    };
  }, [taskInfo]);

  const handleSuccess = () => {
    setSuccessMsg('Task updated');
    setTimeout(() => setSuccessMsg(''), 2000);
  };

  const handleError = () => {
    setErrorMsg('Task update failed');
    setTimeout(() => setErrorMsg(''), 2000);
  };

  const onUpdate = async () => {
    setSuccessMsg('');
    setErrorMsg('');
    setLoading(true);
    // update the example type and caption
    if (location && location.examples) {
      const examples = [...location.examples];
      const updatedExamples = _.map(examples, e => {
        if (e.taskId === taskId) {
          const update: any = {
            isGoodExample: exampleType === 'good',
          };
          update.caption = currentCaption;
          return Object.assign({}, e, update);
        }
        return e;
      });
      // @ts-ignore
      Transforms.setNodes(editor, { examples: updatedExamples }, { at: location.path });
    }
    // update the actual task
    if (taskRef.current) {
      // get current response
      try {
        const response = await taskRef.current.getReviewChanges();
        const res = await API.selfserve.updateInstructionsExample({ task: taskId, response });
        queryClient.invalidateQueries(['use-instruction-task-data', taskId]);
        if (res.success) {
          setTaskProps({ ...taskProps, fixResponse: response });
          handleSuccess();
        } else {
          handleError();
        }
      } catch (e) {
        handleError();
      }
    }
    setLoading(false);

    if (handleUpdateExample) {
      handleUpdateExample({
        taskId,
        caption: currentCaption,
        isGoodExample: exampleType === 'good',
      });
    }
  };

  const close = (event?: any) => {
    // prevent ESC from closing modal
    if (event?.type === 'keydown' && event?.keyCode === 27) return;

    if (lastSelection && lastSelection.anchor) {
      Transforms.select(editor, lastSelection);
    }
    setModalOpen(false);
  };

  return (
    <Dialog
      classes={{ root: readOnly ? classes.zIndexHigh : classes.zIndexLow, paper: classes.dialog }}
      maxWidth="lg"
      open={modalOpen}
      onClose={close}
    >
      <DialogTitle>
        <div className={classes.title}>Example</div>
      </DialogTitle>
      <DialogContent>
        {!taskProps && <h6>No Images to display...</h6>}
        {taskProps && status !== TaskStatus.Completed && (
          <Alert severity="info" style={{ marginTop: '16px' }}>
            Annotations are still being processed
          </Alert>
        )}
        {errorMsg.length > 0 && (
          <Alert severity="error" style={{ marginTop: '2px', marginBottom: '12px' }}>
            {errorMsg}
          </Alert>
        )}
        {successMsg.length > 0 && (
          <Alert severity="success" style={{ marginTop: '2px', marginBottom: '12px' }}>
            {successMsg}
          </Alert>
        )}
        {taskProps && (
          <>
            <ExampleHeader
              exampleType={exampleType}
              setExampleType={(type: string) => {
                if (!readOnly) {
                  setExampleType(type);
                }
              }}
              disabled={readOnly}
              readOnly={readOnly}
            />
            <ErrorBoundary>
              <Suspense fallback={<Spinner style={{ paddingBottom: '16px' }} />}>
                <div className="component">
                  <ShortcutsProvider>
                    <TaskComponent
                      taskType={taskProps.type}
                      taskProps={taskProps}
                      taskRef={taskRef}
                      isTaxonomyDisplay={true}
                    />
                  </ShortcutsProvider>
                </div>
              </Suspense>
            </ErrorBoundary>
          </>
        )}

        <style jsx global>{`
          .component {
            height: 540px;
          }
          .component,
          .component .task {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            min-width: 0;
            width: 100%;
            overflow: auto;
          }
          .task__splitpane {
            position: relative;
            overflow: auto;
          }
        `}</style>

        {allowToEdit && (
          <input
            value={currentCaption}
            placeholder={CAPTION_PLACEHOLDER}
            style={{
              opacity: 0.75,
              marginTop: 12,
              textAlign: 'center',
              border: 0,
              fontSize: '14px',
              width: '100%',
            }}
            onFocus={e => (e.target.placeholder = '')}
            onBlur={e => (e.target.placeholder = CAPTION_PLACEHOLDER)}
            onChange={e => {
              e.preventDefault();
              setCurrentCaption(e.target.value);
            }}
          />
        )}

        {caption && !allowToEdit && (
          <div
            style={{
              marginTop: 12,
              textAlign: 'center',
              background: 'white',
              border: 0,
              fontSize: '14px',
              width: '100%',
            }}
          >
            {caption}
          </div>
        )}
      </DialogContent>
      {allowToEdit ? (
        <DialogActions>
          <Button onClick={() => onUpdate()} disabled={loading || !allowToEdit} type="primary">
            Update
          </Button>
        </DialogActions>
      ) : (
        <></>
      )}
    </Dialog>
  );
};

const useStyles = makeStyles({
  dialog: {
    width: '100%',
    overflow: 'hidden',
    margin: '3% auto 20px',
  },
  zIndexHigh: {
    zIndex: '2000 !important' as any,
  },
  zIndexLow: {
    zIndex: '110 !important' as any,
  },
  title: {
    textAlign: 'center',
  },
});

export default ExampleViewer;
